import React, { useEffect, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Sheet } from "@c4c-web/ui";
// import { useAnalytics } from "@c4c-web/core/hooks/analytics";
// import {
//   EAnalyticsEvent,
//   EAnalyticsEventAction,
//   EAnalyticsEventCategory,
// } from "@c4c-web/core/constants";
import {
  // getDefaultAddressAnalyticsParams,
  getFormFieldRequirements,
  isPhoneNumberValid,
} from "../../../utils";
import type { IAddress } from "../../../types";
import {
  AREA_KEY,
  FLAT_KEY,
  BLOCK_KEY,
  CITY_KEY,
  FLOOR_KEY,
  STREET_KEY,
  ZONE_KEY,
  // EAnalyticsEventLabel,
  // EAnalyticsScreenName,
} from "../../../constants";
import { useAddressForm } from "../../../hooks";
import { ConfirmAddress } from "../ConfirmAddress/ConfirmAddress";
import { getAddressZone } from "../../../services/location.service";
import { validateForm } from "./address-form.helper";
import type { IAddressFormProps, ICity } from "./address-form.interface";
import { AddressTypeSelector } from "./AddressTypeSelector";
import { AddressFormFields } from "./AddressFormFields";
import { ReceiverDetailFields } from "./ReceiverDetailFields";
import { DefaultAddressToggle } from "./DefaultAddressToggle";
import { AddressFormFooter } from "./AddressFormFooter";

export const AddressForm: React.FC<IAddressFormProps> = ({
  address = {},
  coordinates,
  editInfo,
  handleMapAddress,
  openForm,
  setIsSelected,
  setIsTyping,
  setShowMap,
  storeId,
  t,
  toggleForm,
}) => {
  const {
    addressFormSetters,
    addressFormStates,
    errors,
    getValues,
    handleSubmit,
    register,
    reset,
  } = useAddressForm(address);

  const formData = getValues();
  // const { sendEvent } = useAnalytics();

  useEffect(() => {
    const phoneNumber = `${formData.countryCode}${formData.phone}`;
    const isPhoneValid = isPhoneNumberValid(phoneNumber);
    if (isPhoneValid)
      addressFormSetters.setErrorStates((prevStates) => ({
        ...prevStates,
        invalidPhoneNumber: false,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unnecessary dependency
  }, [formData.countryCode, formData.phone]);

  const setAddressType = (values: string[]): void => {
    addressFormSetters.setOtherStates((prevStates) => ({
      ...prevStates,
      addressType: values,
    }));
    // sendEvent(
    //   {
    //     event: EAnalyticsEvent.Custom,
    //     event_action: EAnalyticsEventAction.AddressJourney,
    //     event_category: EAnalyticsEventCategory.UserEngagement,
    //     event_label: EAnalyticsEventLabel.AddressTypeSelected,
    //   },
    //   getDefaultAddressAnalyticsParams(
    //     EAnalyticsScreenName.AddAddressDetailsScreen,
    //   ),
    // );
  };

  const clearForm = (): void => {
    reset();
    setAddressType([""]);
    addressFormSetters.setReceiverDetails((prevStates) => ({
      ...prevStates,
      changeReceiverDetails: false,
    }));
    addressFormSetters.setOtherStates((prevStates) => ({
      ...prevStates,
      defaultAddress: false,
    }));
  };

  const closeDrawer = (): void => {
    toggleForm(false);
  };

  const handleCancel = (): void => {
    clearForm();
    closeDrawer();
  };

  const onSubmit: SubmitHandler<IAddress> = (data) => {
    const isFormValid = validateForm(
      addressFormSetters,
      addressFormStates,
      formData,
      storeId,
      t,
    );
    if (isFormValid) {
      const {
        appartment,
        building,
        countryCode,
        firstName,
        isPhoneVerified,
        phone,
        landmark,
        streetName,
        town,
        otherAddressType,
      } = data;
      let formattedAddress: IAddress = {
        addressLabel: addressFormStates.otherStates.addressType[0],
        appartment,
        building,
        country: { isocode: address.country?.isocode || "" },
        countryCode,
        defaultAddress: addressFormStates.otherStates.defaultAddress,
        deliveryInstructions: address.deliveryInstructions,
        emirate: address.emirate || "",
        firstName,
        id: address.id || "",
        isPhoneVerified: isPhoneVerified || false,
        landmark,
        latitude: address.latitude || "",
        longitude: address.longitude || "",
        phone: `${countryCode}${phone}`,
        streetName,
        town: address.town || town,
        editInfo:
          (editInfo?.id ?? address.editInfo?.id)
            ? editInfo || address.editInfo
            : undefined,
        otherAddressType,
      };
      const { isKuwaitFormFieldRequired, isQatarFormFieldRequired } =
        getFormFieldRequirements(storeId);
      if (isKuwaitFormFieldRequired) {
        const { number, floor, block } = data;
        let kuwaitAppartment = "";
        let kuwaitBuilding = "";
        if (block) kuwaitBuilding = `${building}|${BLOCK_KEY}${block}`;
        if (floor)
          kuwaitAppartment = `${FLOOR_KEY}${floor}|${FLAT_KEY}${number}`;
        formattedAddress = {
          ...formattedAddress,
          appartment: kuwaitAppartment,
          building: kuwaitBuilding,
        };
      } else if (isQatarFormFieldRequired) {
        const { selectedCity, selectedZone, selectedArea } =
          addressFormStates.qatarStates;
        formattedAddress = {
          ...formattedAddress,
          town: selectedArea,
          makani: `${ZONE_KEY}${selectedZone} | ${AREA_KEY}${selectedArea} | ${CITY_KEY}${selectedCity} | ${STREET_KEY}${streetName}`,
        };
      }
      addressFormSetters.setOtherStates((prevStates) => ({
        ...prevStates,
        finalAddress: formattedAddress,
        showConfirmAddressSheet: true,
      }));
      closeDrawer();
    } else {
      // sendEvent(
      //   {
      //     event: EAnalyticsEvent.Custom,
      //     event_action: EAnalyticsEventAction.AddressJourney,
      //     event_category: EAnalyticsEventCategory.UserEngagement,
      //     event_label: EAnalyticsEventLabel.AddressDetailsError,
      //   },
      //   getDefaultAddressAnalyticsParams(
      //     EAnalyticsScreenName.AddAddressDetailsScreen,
      //   ),
      // );
    }
  };

  const submitForm = async (): Promise<void> => {
    // sendEvent(
    //   {
    //     event: EAnalyticsEvent.Custom,
    //     event_action: EAnalyticsEventAction.AddressJourney,
    //     event_category: EAnalyticsEventCategory.UserEngagement,
    //     event_label: EAnalyticsEventLabel.AddressDetailsSubmitted,
    //   },
    //   getDefaultAddressAnalyticsParams(
    //     EAnalyticsScreenName.AddAddressDetailsScreen,
    //   ),
    // );
    validateForm(addressFormSetters, addressFormStates, formData, storeId, t);
    await handleSubmit(onSubmit)();
  };

  const toggleConfirmAddressSheet = (value: boolean): void => {
    addressFormSetters.setOtherStates((prevStates) => ({
      ...prevStates,
      showConfirmAddressSheet: value,
    }));
  };

  const [qatarCities, setQatarCities] = useState<ICity[]>([]);

  useEffect(() => {
    if (!openForm) return;

    const { isQatarFormFieldRequired } = getFormFieldRequirements(storeId);
    if (isQatarFormFieldRequired) {
      const fetchCities = async (): Promise<void> => {
        try {
          const data = await getAddressZone();
          setQatarCities(data.data.cities);
        } catch (error) {
          // eslint-disable-next-line no-console -- logger
          console.error("Failed to load Qatar cities", error);
        }
      };

      void fetchCities();
    }
  }, [openForm]);
  return (
    <>
      <Sheet
        className="h-full max-h-screen rounded-none md:w-[480px] md:rounded-l-2xl"
        footer={
          <AddressFormFooter
            handleCancel={handleCancel}
            submitForm={submitForm}
            t={t}
          />
        }
        hideScrollBar
        id="address-form-sheet"
        isTitleBorder={false}
        onOpenChange={toggleForm}
        open={openForm}
        title={t("addressJourney.addAddressDetails")}
      >
        <form className="flex flex-col gap-sm">
          <AddressTypeSelector
            addressType={addressFormStates.otherStates.addressType}
            addressTypeError={addressFormStates.errorStates.addressTypeError}
            errors={errors}
            formData={formData}
            register={register}
            setAddressType={setAddressType}
            t={t}
          />

          <AddressFormFields
            address={address}
            addressFormSetters={addressFormSetters}
            addressFormStates={addressFormStates}
            cities={qatarCities}
            editInfo={editInfo}
            errors={errors}
            formData={formData}
            handleMapAddress={handleMapAddress}
            register={register}
            setIsSelected={setIsSelected}
            setIsTyping={setIsTyping}
            setShowMap={setShowMap}
            storeId={storeId}
            t={t}
            toggleForm={toggleForm}
          />

          <ReceiverDetailFields
            address={address}
            addressFormSetters={addressFormSetters}
            addressFormStates={addressFormStates}
            errors={errors}
            formData={formData}
            register={register}
            storeId={storeId}
            t={t}
          />

          <DefaultAddressToggle
            addressFormSetters={addressFormSetters}
            addressFormStates={addressFormStates}
            t={t}
          />
        </form>
      </Sheet>

      {addressFormStates.otherStates.showConfirmAddressSheet ? (
        <ConfirmAddress
          address={addressFormStates.otherStates.finalAddress}
          addressFormSetters={addressFormSetters}
          clearForm={clearForm}
          coordinates={coordinates}
          onOpenChange={toggleConfirmAddressSheet}
          open={addressFormStates.otherStates.showConfirmAddressSheet}
          openAddressForm={toggleForm}
          storeId={storeId}
          t={t}
        />
      ) : null}
    </>
  );
};
